import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import i18n from '../i18n'
import { DEFAULT_LOCALE } from '../utils/browserLocale'
import { publicRoutes, privateRoutes, authRoutes } from './routes'
import { seoService } from '../services/seo'
import { getRouteSeoMeta, isSelfManagedSeoRoute, getRouteMetaFromConfig } from '../config/seo.i18n'
import { defaultSeoConfig } from '../config/seo'
import { buildAbsoluteCanonicalUrl } from '../utils/canonicalUrl'

function normalizePathForCanonical(path: string): string {
  if (path === '' || path === '/') return '/'
  return path.endsWith('/') ? path : `${path}/`
}

function toAbsolutePublicPath(routePath: string): string {
  if (routePath === '' || routePath === '/') return '/'
  return routePath.startsWith('/') ? routePath : `/${routePath}`
}

/** Routes publiques sans préfixe de langue (/services, /blog/…, / = accueil). */
const publicRoutesAbsolute: RouteRecordRaw[] = publicRoutes.map((route) => ({
  ...route,
  path: toAbsolutePublicPath(route.path),
  name: route.name as string
}))

const routes: RouteRecordRaw[] = [
  { path: '/fr/', redirect: { path: '/', replace: true } },
  { path: '/fr', redirect: { path: '/', replace: true } },
  {
    path: '/fr/:path(.*)',
    redirect: (to) => {
      const raw = (to.params.path as string) || ''
      const tail = raw.replace(/^\/+/, '')
      return tail ? `/${tail}` : '/'
    }
  },
  ...publicRoutesAbsolute,
  ...privateRoutes,
  ...authRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue'),
    meta: (() => {
      const config = getRouteMetaFromConfig('not-found')
      return {
        title: config?.title ?? 'Page non trouvée | PostDev',
        description: config?.description ?? 'La page que vous recherchez n\'existe pas',
        robots: config?.robots ?? 'noindex,follow'
      }
    })()
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  try {
    const legacyMatch = to.path.match(/^\/(en|es|de|it|pt|nl|pl)(\/.*)?$/)
    if (legacyMatch) {
      const tail = legacyMatch[2] || '/'
      next({ path: tail === '/' ? '/' : tail, replace: true })
      return
    }

    const routeNameStr = String(to.name || '')
    if (routeNameStr === 'services' || routeNameStr.startsWith('services-')) {
      const { ensureMotionPlugin } = await import('../utils/motionLoader')
      await ensureMotionPlugin()
    }

    i18n.global.locale.value = 'fr'
    document.documentElement.setAttribute('lang', 'fr')

    const safeLocale = DEFAULT_LOCALE
    seoService.setLocale('fr')

    const authStore = useAuthStore()

    if (!authStore.isInitialized) {
      await authStore.initializeAuth()
    }

    authStore.syncAuthState()

    if (!authStore.user && authStore.token && !authStore.isTokenExpired) {
      try {
        await authStore.fetchCurrentUser()
      } catch (error) {
        console.warn('⚠️ Impossible de récupérer les informations utilisateur:', error)
        authStore.clearStorage()
      }
    }

    const isAuthenticated = authStore.isAuthenticated
    const isAdmin = authStore.isAdmin

    if (to.meta.requiresAuth && !isAuthenticated) {
      next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
      return
    }

    if (to.meta.requiresAdmin && !isAdmin) {
      console.warn('🚫 Accès refusé : privilèges administrateur requis')
      next({ name: 'not-found' })
      return
    }

    if (isAuthenticated && (to.name === 'login' || to.name?.toString().startsWith('login-'))) {
      next({ name: 'dashboard' })
      return
    }

    const routeName = String(to.name || '')
    const selfManagedSeo = isSelfManagedSeoRoute(routeName)

    if (!selfManagedSeo) {
      const localizedMeta = getRouteSeoMeta(routeName, safeLocale)
      const title = localizedMeta?.title || (to.meta.title as string) || 'PostDev'
      const description =
        localizedMeta?.description || (to.meta.description as string | undefined)

      document.title = title
      seoService.setTitle(title)
      if (description) {
        seoService.setDescription(description)
      }

      const seoBase = defaultSeoConfig.canonicalUrl.replace(/\/$/, '')
      seoService.setCanonicalUrl(buildAbsoluteCanonicalUrl(seoBase, normalizePathForCanonical(to.path)))
    }

    const metaRobots = document.querySelector('meta[name="robots"]')
    if (metaRobots && to.meta.robots) {
      metaRobots.setAttribute('content', to.meta.robots as string)
    }

    // ── JSON-LD automatique pour les pages d'outils ────────────────────────
    document.querySelector('script[data-seo-tool]')?.remove()
    if (routeName.startsWith('tool-') && to.meta.toolId) {
      const toolTitle  = to.meta.title as string
      const toolDesc   = to.meta.description as string
      const seoBase    = defaultSeoConfig.canonicalUrl.replace(/\/$/, '')
      const toolUrl    = `${seoBase}${to.path.endsWith('/') ? to.path : to.path + '/'}`

      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-seo-tool', 'true')

      const jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebApplication',
            '@id': `${toolUrl}#app`,
            name: toolTitle.split(' | ')[0].split(' — ')[0],
            description: toolDesc,
            url: toolUrl,
            applicationCategory: 'UtilitiesApplication',
            operatingSystem: 'Web',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
            provider: { '@type': 'Organization', name: 'PostDev', url: seoBase }
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${toolUrl}#breadcrumb`,
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${seoBase}/` },
              { '@type': 'ListItem', position: 2, name: 'Outils', item: `${seoBase}/outils/` },
              { '@type': 'ListItem', position: 3, name: toolTitle.split(' | ')[0].split(' — ')[0], item: toolUrl }
            ]
          }
        ]
      }

      try {
        script.textContent = JSON.stringify(jsonLd)
      } catch {
        script.textContent = '{}'
      }
      document.head.appendChild(script)
    }

    next()
  } catch (error) {
    console.error('❌ Erreur dans le router guard:', error)
    next()
  }
})

router.afterEach((to) => {
  setTimeout(() => {
    seoService.updateLanguageAlternates()
    document.documentElement.lang = DEFAULT_LOCALE
    const main = document.getElementById('main-content')
    if (main) {
      main.focus({ preventScroll: true })
    }
  }, 0)
})

export default router
